import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, IconButton, Pagination,
  Paper, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { deleteTransportadora, getAllTransportadoras } from '../services/api';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormTransportadora from './FormTransportadora';
import { getApiErrorMessage } from '../utils/apiError';
import SortableTh from './common/SortableTh';
import { useServerSort } from '../hooks/useServerSort';

const onlyDigits = (v) => String(v || '').replace(/\D/g, '');
const fmtCNPJ = (v) => {
  const d = onlyDigits(v).slice(0, 14);
  if (!d) return '-';
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

export default function TransportadoraList() {
  const [pagina, setPagina] = useState(1);
  const [itensPorPagina] = useState(20);
  const [itens, setItens] = useState([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [selecionados, setSelecionados] = useState([]);
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [idsParaExcluir, setIdsParaExcluir] = useState([]);
  const [form, setForm] = useState({ open: false, id: null });
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const { orderBy, order, requestSort: aplicarOrdem } = useServerSort('razaoSocial', 'asc');
  const requestSort = (key) => {
    setPagina(1);
    aplicarOrdem(key);
  };

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const { data } = await getAllTransportadoras({
        indice: pagina,
        qtde: itensPorPagina,
        termo: searchTerm.trim(),
        ativo: true,
        ordenar: orderBy,
        direcao: order,
      });
      setItens(data.items || []);
      setTotalPaginas(data.totalPages || 1);
      setTotalItens(data.totalItems || 0);
      setSelecionados([]);
    } catch (e) {
      setSnack({
        open: true,
        severity: 'error',
        msg: getApiErrorMessage(e, 'Erro ao carregar transportadoras'),
      });
    } finally {
      setCarregando(false);
    }
  }, [pagina, itensPorPagina, searchTerm, orderBy, order]);

  useEffect(() => { carregar(); }, [carregar]);

  const itensOrdenados = itens;

  const todosSelecionados = useMemo(
    () => itens.length > 0 && selecionados.length === itens.length,
    [itens, selecionados],
  );

  const excluir = async () => {
    try {
      await Promise.all(idsParaExcluir.map((id) => deleteTransportadora(id)));
      setSnack({
        open: true,
        severity: 'success',
        msg: idsParaExcluir.length > 1
          ? `${idsParaExcluir.length} transportadoras desativadas`
          : 'Transportadora desativada',
      });
      await carregar();
    } catch (e) {
      setSnack({
        open: true,
        severity: 'error',
        msg: getApiErrorMessage(e, 'Erro ao desativar'),
      });
    } finally {
      setDialogoAberto(false);
      setIdsParaExcluir([]);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Transportadoras"
        subtitle="Cadastro usado no frete da NF-e."
        actions={
          <>
            {selecionados.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  setIdsParaExcluir(selecionados);
                  setDialogoAberto(true);
                }}
              >
                Desativar ({selecionados.length})
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setForm({ open: true, id: null })}>
              Nova transportadora
            </Button>
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Buscar por razão social, CNPJ ou cidade..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPagina(1);
              setSearchTerm(searchText);
            }
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: searchText && (
              <IconButton size="small" onClick={() => setSearchText('')}>
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }}
          sx={{ flex: 2, minWidth: 280 }}
        />
        <Button
          variant="outlined"
          onClick={() => {
            setPagina(1);
            setSearchTerm(searchText);
          }}
        >
          Buscar
        </Button>
      </Paper>

      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selecionados.length > 0 && !todosSelecionados}
                  checked={todosSelecionados}
                  onChange={(e) => setSelecionados(e.target.checked ? itens.map((i) => i.id) : [])}
                />
              </TableCell>
              <SortableTh id="razaoSocial" label="Razão social" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cnpj" label="CNPJ" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cidadeUf" label="Cidade/UF" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : itens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma transportadora encontrada
                </TableCell>
              </TableRow>
            ) : itensOrdenados.map((t) => (
              <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => setForm({ open: true, id: t.id })}>
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selecionados.includes(t.id)}
                    onChange={() => setSelecionados((prev) => (
                      prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id]
                    ))}
                  />
                </TableCell>
                <TableCell data-label="Razão social">{t.razaoSocial || '-'}</TableCell>
                <TableCell data-label="CNPJ">{fmtCNPJ(t.cnpj)}</TableCell>
                <TableCell data-label="Cidade/UF">
                  {t.cidade || '-'}{t.estado ? `/${t.estado}` : ''}
                </TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Editar">
                    <IconButton color="primary" onClick={() => setForm({ open: true, id: t.id })}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Desativar">
                    <IconButton
                      color="error"
                      onClick={() => {
                        setIdsParaExcluir([t.id]);
                        setDialogoAberto(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Stack spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={(_e, p) => setPagina(p)} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {itens.length} de {totalItens} (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <EntityFormDialog
        open={form.open}
        title={form.id ? 'Editar transportadora' : 'Nova transportadora'}
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={() => setForm((f) => ({ ...f, open: false }))}
        maxWidth="md"
      >
        <FormTransportadora
          key={form.id || 'nova'}
          embedded
          transportadoraId={form.id}
          onCancel={() => setForm((f) => ({ ...f, open: false }))}
          onSaved={(msg) => {
            setForm({ open: false, id: null });
            setSnack({ open: true, severity: 'success', msg });
            carregar();
          }}
        />
      </EntityFormDialog>

      <Dialog open={dialogoAberto} onClose={() => setDialogoAberto(false)}>
        <DialogTitle>Desativar transportadora</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {idsParaExcluir.length > 1
              ? `Desativar ${idsParaExcluir.length} transportadoras?`
              : 'Desativar esta transportadora?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoAberto(false)}>Cancelar</Button>
          <Button color="error" onClick={excluir}>Desativar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
